import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { User, Mail, Phone, Calendar, Shield, Edit2, Check, X, ArrowLeft } from 'lucide-react';

const ProfilePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || ''
  });

  const handleSave = () => {
    // In a real app, this would update the user profile via API
    toast.success('Profile updated successfully!');
    setEditing(false);
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">Please login to view your profile</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Back Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-4 text-slate-600 hover:text-slate-900 -ml-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-outfit font-bold text-slate-900">My Profile</h1>
            <p className="text-slate-500 mt-1">Manage your account information</p>
          </div>

          {/* Profile Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            {/* Profile Header */}
            <div className="bg-gradient-to-r from-[#0047AB] to-[#003380] px-6 py-8">
              <div className="flex items-center gap-4">
                {user.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name} 
                    className="w-20 h-20 rounded-full border-4 border-white shadow-lg"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center text-3xl font-bold text-[#0047AB] border-4 border-white shadow-lg">
                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                )}
                <div className="text-white">
                  <h2 className="text-xl font-outfit font-bold">{user.name}</h2>
                  <p className="text-white/80 text-sm">{user.email}</p>
                  {user.auth_provider === 'google' && (
                    <span className="inline-flex items-center gap-1 mt-2 text-xs bg-white/20 px-2 py-1 rounded-full">
                      <Shield className="w-3 h-3" />
                      Verified via Google
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Profile Details */}
            <div className="p-6 space-y-6">
              {/* Edit Toggle */}
              <div className="flex justify-end">
                {editing ? (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditing(false)}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      className="bg-[#0047AB] hover:bg-[#003380]"
                      onClick={handleSave}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Save
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditing(true)}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit Profile
                  </Button>
                )}
              </div>

              {/* Info Grid */}
              <div className="space-y-4">
                {/* Name */}
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="p-3 bg-white rounded-lg shadow-sm">
                    <User className="w-5 h-5 text-[#0047AB]" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500 uppercase tracking-wide">Full Name</Label>
                    {editing ? (
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1"
                      />
                    ) : (
                      <p className="font-medium text-slate-900">{user.name}</p>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="p-3 bg-white rounded-lg shadow-sm">
                    <Mail className="w-5 h-5 text-[#0047AB]" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500 uppercase tracking-wide">Email Address</Label>
                    <p className="font-medium text-slate-900">{user.email}</p>
                    <p className="text-xs text-slate-400 mt-0.5">Email cannot be changed</p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="p-3 bg-white rounded-lg shadow-sm">
                    <Phone className="w-5 h-5 text-[#0047AB]" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500 uppercase tracking-wide">Phone Number</Label>
                    {editing ? (
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+91 9876543210"
                        className="mt-1"
                      />
                    ) : (
                      <p className="font-medium text-slate-900">{user.phone || 'Not provided'}</p>
                    )}
                  </div>
                </div>

                {/* Account Created */}
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="p-3 bg-white rounded-lg shadow-sm">
                    <Calendar className="w-5 h-5 text-[#0047AB]" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500 uppercase tracking-wide">Member Since</Label>
                    <p className="font-medium text-slate-900">
                      {user.created_at 
                        ? new Date(user.created_at).toLocaleDateString('en-IN', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })
                        : 'Recently joined'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProfilePage;
